import { ITEM_TABLE_TYPE, SCALING_TABLE_TYPE } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import _ from 'lodash';
import { TableTypeEnum } from '@/sdk/model/TableDefinitionInputV1';
import type { SelectedSearchItem } from '@/search/search.types';
import { sqHierarchiesApi } from '@/sdk/api/HierarchiesApi';
import { sqTableDefinitionsApi } from '@/sdk/api/TableDefinitionsApi';
import type { HierarchyNodeOutputV1 } from '@/sdk/model/HierarchyNodeOutputV1';
import type { ItemPreviewV1 } from '@/sdk/model/ItemPreviewV1';
import type { ItemSearchPreviewV1 } from '@/sdk/model/ItemSearchPreviewV1';
import type { ColumnDefinitionOutputV1 } from '@/sdk/model/ColumnDefinitionOutputV1';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionOutputV1';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import type { ItemTableCell, ProcessedTableCell } from '@/tableDefinitionEditor/tableDefinition.types';
import {
  getColumnsNotIncludedInAgGrid,
  getMaterializedTable,
  isItemTableCell,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import { HierarchyNodeType, TABLE_DEFINITION_SUBTYPES } from '@/search/search.constants';
import type { TableDefinitionOutputV1 } from '@/sdk/model/TableDefinitionOutputV1';
import { GraphQL } from '@/serverSideTable/materializedTables/materializedTables.constants';

/*
 * Limit of items shown in the overview mode of the Search Widget
 */
export const LIMIT_IN_OVERVIEW = 8;

/**
 * Checks if search types are extracted, as these types can't be mixed with other item types. TableDefinition
 * Types have a frontend Pseudo-type to check as well.
 * An invalid result is defined as combining {@link SCALING_TABLE_TYPE} or {@link ITEM_TABLE_TYPE} or
 * {@link SeeqName.Types.Hierarchy} with other search types.
 */
export const hasInvalidSearchTypes = (types: string[]) => {
  const onlyTableTypes = types.every((type) => TABLE_DEFINITION_SUBTYPES.includes(type));
  const onlyHierarchyType = types.every((type) => type === SeeqNames.Types.Hierarchy);
  if (onlyTableTypes || onlyHierarchyType) {
    return false;
  }

  return (
    types.length > 1 &&
    types.some((type) => TABLE_DEFINITION_SUBTYPES.includes(type) || type === SeeqNames.Types.Hierarchy)
  );
};

export const filterOutInvalidSearchTypes = (types: string[] | undefined): string[] | undefined => {
  if (!types || !hasInvalidSearchTypes(types)) {
    return types;
  }
  return types.filter((type) => !TABLE_DEFINITION_SUBTYPES.includes(type) && type !== SeeqNames.Types.Hierarchy);
};

export const replacePseudoTypesWithBackendType = (searchTypes: string[] | string): string | string[] => {
  if (!searchTypes) {
    return searchTypes;
  }
  if (!_.isArray(searchTypes)) {
    return TABLE_DEFINITION_SUBTYPES.includes(searchTypes) ? SeeqNames.Types.TableDefinition : searchTypes;
  }
  return _.chain(searchTypes)
    .map((type) => (TABLE_DEFINITION_SUBTYPES.includes(type) ? SeeqNames.Types.TableDefinition : type))
    .uniqBy((type) => type)
    .value();
};

const SCALING_CSV_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.SCALINGCSV}`;
const SCALING_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.SCALING}`;
const ITEM_CSV_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.ITEMCSV}`;
const ITEM_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.ITEM}`;
type TableTypeToFilter = 'Scaling' | 'Item' | 'Both';

const transformSingleFilterToIncludeTableType = (filter: string, type: TableTypeToFilter): string[] => {
  if (type === 'Both') {
    return [
      filter.concat(SCALING_FILTER),
      filter.concat(SCALING_CSV_FILTER),
      filter.concat(ITEM_FILTER),
      filter.concat(ITEM_CSV_FILTER),
    ];
  }
  if (type === 'Scaling') {
    return [filter.concat(SCALING_FILTER), filter.concat(SCALING_CSV_FILTER)];
  } else {
    return [filter.concat(ITEM_FILTER), filter.concat(ITEM_CSV_FILTER)];
  }
};

export const updateTypesAndFiltersForTableDefinitions = (
  searchTypes: string[] | string,
  currentFilters: string[] | string,
): { updatedTypes: string[] | string; updatedFilters: string[] | string } => {
  let type: TableTypeToFilter = 'Both';
  if (_.includes(searchTypes, SCALING_TABLE_TYPE) && !_.includes(searchTypes, ITEM_TABLE_TYPE)) {
    type = 'Scaling';
  } else if (!_.includes(searchTypes, SCALING_TABLE_TYPE) && _.includes(searchTypes, ITEM_TABLE_TYPE)) {
    type = 'Item';
  }
  let updatedFilters = addFilterForTableDefinitionSubTypes(searchTypes, currentFilters, type);
  let updatedTypes = replacePseudoTypesWithBackendType(searchTypes);
  return { updatedTypes, updatedFilters };
};

export const addFilterForTableDefinitionSubTypes = (
  searchTypes: string[] | string,
  currentFilters: string[] | string,
  type: TableTypeToFilter,
): string | string[] => {
  if (
    !_.includes(searchTypes, SeeqNames.Types.TableDefinition) &&
    !_.includes(searchTypes, SCALING_TABLE_TYPE) &&
    !_.includes(searchTypes, ITEM_TABLE_TYPE)
  ) {
    return currentFilters;
  }
  if (typeof currentFilters === 'string') {
    if (currentFilters.includes(SeeqNames.API.Flags.ExcludeGloballyScoped)) {
      return [currentFilters].concat(transformSingleFilterToIncludeTableType('', type));
    }
    return transformSingleFilterToIncludeTableType(currentFilters, type);
  }

  const [potentialExcludeGloballyScoped, filters] = _.partition(
    currentFilters,
    (filter) => filter === SeeqNames.API.Flags.ExcludeGloballyScoped,
  );
  // If the search type is TableDefinition, but filters are empty, we still need to add the filters.
  const filtersToProcess = filters.length === 0 ? [''] : filters;
  return potentialExcludeGloballyScoped.concat(
    filtersToProcess.flatMap((filter) => transformSingleFilterToIncludeTableType(filter, type)),
  );
};

export const getScalingTableLayerForDataTab = async (
  searchItem: SelectedSearchItem | ItemSearchPreviewV1,
  scalingTableIsCsv = false,
): Promise<SelectedSearchItem[]> => {
  if (searchItem.type !== SeeqNames.Types.TableDefinition) {
    const scalingTable = searchItem.ancestors!.find((ancestor) => ancestor.type === SeeqNames.Types.TableDefinition);
    return getScalingTableRowAsChildren(searchItem, scalingTable!);
  }
  return getScalingTableIndependentVariables(searchItem, scalingTableIsCsv);
};

/**
 * Fetches the independent variables from a scaling table.
 * @param scalingTableDefinition
 * @param scalingTableIsCsv
 */
const getScalingTableIndependentVariables = async (
  scalingTableDefinition: SelectedSearchItem | ItemSearchPreviewV1,
  scalingTableIsCsv = false,
): Promise<SelectedSearchItem[]> => {
  const { data: scalingTableData } = await sqTableDefinitionsApi.getTableDefinition({ id: scalingTableDefinition.id });
  const columns = scalingTableData.columnDefinitions;
  const includedColumn = scalingTableIsCsv
    ? SeeqNames.MaterializedTables.CsvItemIdColumn
    : SeeqNames.MaterializedTables.ItemIdColumn;

  let includedColumns: string[] = [includedColumn, SeeqNames.MaterializedTables.DatumIdColumn];
  let propertyRequests: { uuidColumn: string; propertyNames: string[] }[] = [
    {
      uuidColumn: includedColumn,
      propertyNames: [SeeqNames.Properties.Name, SeeqNames.Properties.Type],
    },
  ];

  let navigationColumn: ColumnDefinitionOutputV1 | undefined;
  if (scalingTableData.navigationColumnId) {
    navigationColumn = columns.find((col) => col.id === scalingTableData.navigationColumnId);
    if (navigationColumn && !includedColumns.includes(navigationColumn.columnName)) {
      includedColumns.push(navigationColumn.columnName);
      if (navigationColumn.columnType === ColumnTypeEnum.UUID) {
        const displayProperty = navigationColumn.displayProperty
          ? navigationColumn.displayProperty
          : SeeqNames.Properties.Name;
        propertyRequests.push({
          uuidColumn: navigationColumn.columnName,
          propertyNames: [displayProperty, SeeqNames.Properties.Type],
        });
      }
    }
  }
  const { data } = await getMaterializedTable(scalingTableDefinition.id, includedColumns, propertyRequests);

  const independentVariableColumnId = columns.find((col) => col.columnName === includedColumn)?.id;
  const datumIdColumnIndex = data.table.headers.findIndex(
    (header: any) => header.name === SeeqNames.MaterializedTables.DatumIdColumn,
  );
  const idIndex = data.table.headers.findIndex((header: any) => header.name === includedColumn);
  const nameIndex = data.table.headers.findIndex((header: any) => {
    if (navigationColumn) {
      if (navigationColumn.columnType === ColumnTypeEnum.UUID) {
        const displayProperty = navigationColumn.displayProperty
          ? navigationColumn.displayProperty
          : SeeqNames.Properties.Name;
        return header.name === `${navigationColumn.columnName}.properties.${displayProperty}`;
      }
      return header.name === navigationColumn.columnName;
    }
    return header.name === `${includedColumn}.properties.${SeeqNames.Properties.Name}`;
  });
  const typeIndex = data.table.headers.findIndex(
    (header: any) => header.name === `${includedColumn}.properties.${SeeqNames.Properties.Type}`,
  );

  return _.chain(data?.table.rows)
    .filter((row: any) => {
      return row[0] !== null;
    })
    .map((row: any) => {
      return {
        id: row[idIndex],
        name: typeof row[nameIndex] === 'string' ? row[nameIndex] : row[nameIndex]?.toString(),
        type: row[typeIndex],
        columnId: independentVariableColumnId,
        ancestors: [
          {
            id: scalingTableDefinition.id,
            name: scalingTableDefinition.name,
            type: scalingTableDefinition.type,
          },
        ],
        datumId: row[datumIdColumnIndex],
      } as SelectedSearchItem;
    })
    .sortBy('name')
    .value();
};

/**
 * Fetches the whole row of cell values from a scaling table of trendable columns.
 * @param tableSearchItem
 * @param scalingTableDefinition
 */
const getScalingTableRowAsChildren = async (
  tableSearchItem: SelectedSearchItem,
  scalingTableDefinition: ItemPreviewV1,
): Promise<SelectedSearchItem[]> => {
  const { materializedTableRow, scalingTableData } = await getItemsInScalingTableRow(
    scalingTableDefinition.id,
    tableSearchItem.id,
  );

  return (
    _.chain(materializedTableRow)
      ?.filter((cell: ItemTableCell) => cell.fetchedProperties?.Type !== SeeqNames.Types.Asset)
      .map((cell: ItemTableCell) => {
        const columnName = cell.columnName;
        const columnId = scalingTableData.columnDefinitions.find((def) => def.columnName === columnName)?.id;
        return {
          id: cell.uuid,
          name: cell.fetchedProperties?.Name,
          type: cell.fetchedProperties?.Type,
          columnId,
          ancestors: [
            ...tableSearchItem.ancestors!,
            {
              id: tableSearchItem.id,
              name: tableSearchItem.name,
              type: tableSearchItem.type,
              datumId: tableSearchItem.datumId,
              isScalingTableCSVFlow: tableSearchItem.isScalingTableCSVFlow,
            },
          ],
        } as SelectedSearchItem;
      })
      .sortBy('name')
      .value() || []
  );
};

/**
 * Fetches the whole row of cell values from a scaling table, only returning item cells.
 * @param tableDefinitionId
 * @param rowId - the id of the cell in the item id or csv item id column
 */
export const getItemsInScalingTableRow = async (
  tableDefinitionId: string,
  rowId: string,
): Promise<{ materializedTableRow: ItemTableCell[]; scalingTableData: TableDefinitionOutputV1 }> => {
  const { data: scalingTableData } = await sqTableDefinitionsApi.getTableDefinition({ id: tableDefinitionId });
  const scalingTableIsCsv = scalingTableData.tableType === TableTypeEnum.SCALINGCSV;
  const columnsNotIncludedInAgGrid = getColumnsNotIncludedInAgGrid(
    scalingTableData.tableType,
    scalingTableData.columnDefinitions,
  );
  const uuidColumns = scalingTableData.columnDefinitions
    .filter((column) => column.columnType === ColumnTypeEnum.UUID)
    .filter((column) => !columnsNotIncludedInAgGrid.includes(column.columnName));

  const navigationColumn = scalingTableData.navigationColumnId
    ? scalingTableData.columnDefinitions.find((col) => col.id === scalingTableData.navigationColumnId)
    : undefined;

  const columnNames = [
    ...uuidColumns.map((col) => col.columnName),
    ...(navigationColumn && !uuidColumns.some((col) => col.columnName === navigationColumn.columnName)
      ? [navigationColumn.columnName]
      : []),
  ];

  const propertyRequests: { uuidColumn: string; propertyNames: string[] }[] = [
    ...uuidColumns.map((col) => ({
      uuidColumn: col.columnName,
      propertyNames: [SeeqNames.Properties.Name, SeeqNames.Properties.Type],
    })),
  ];

  if (navigationColumn && navigationColumn.columnType === ColumnTypeEnum.UUID) {
    const navIndex = propertyRequests.findIndex((req) => req.uuidColumn === navigationColumn.columnName);
    const displayProperty = navigationColumn.displayProperty || SeeqNames.Properties.Name;
    if (navIndex !== -1) {
      propertyRequests[navIndex].propertyNames = _.uniq([
        SeeqNames.Properties.Name,
        SeeqNames.Properties.Type,
        displayProperty,
      ]);
    } else {
      propertyRequests.push({
        uuidColumn: navigationColumn.columnName,
        propertyNames: _.uniq([SeeqNames.Properties.Name, SeeqNames.Properties.Type, displayProperty]),
      });
    }
  }

  const { data } = await getMaterializedTable(
    tableDefinitionId,
    columnNames,
    propertyRequests,
    undefined,
    undefined,
    undefined,
    {
      valueFilter: {
        filterType: GraphQL.FilterTypes.Equals,
        columnName: scalingTableIsCsv
          ? SeeqNames.MaterializedTables.CsvItemIdColumn
          : SeeqNames.MaterializedTables.ItemIdColumn,
        value: rowId,
      },
    },
  );

  const processedMaterializedTable = processMaterializedTable(
    data?.table,
    scalingTableData.columnDefinitions.map((column) => {
      return { ...column, displayName: column.columnName, displayProperty: column.displayProperty };
    }),
  );

  return {
    materializedTableRow: processedMaterializedTable?.rows[0]
      .map((row, index) => {
        return {
          ...row,
          columnName: processedMaterializedTable.headers[index].name,
        };
      })
      .filter((cell: ProcessedTableCell) => isItemTableCell(cell)) as ItemTableCell[],
    scalingTableData,
  };
};

const nodeToSelectedSearchItem = (node: HierarchyNodeOutputV1, ancestors: SelectedSearchItem[]): SelectedSearchItem => {
  const id = node.itemMetadata ? node.itemMetadata.id : `${node.displayName}-${node.columnId}`;
  return {
    id,
    node,
    name: node.displayName,
    type: HierarchyNodeType,
    ancestors,
  } as SelectedSearchItem;
};

/**
 * Converts a hierarchy node and its path to a SelectedSearchItem, including all ancestors.
 * @param node - current node
 * @param nodePath = path from root to the current node
 * @param hierarchyOutput - hierarchy this node path lives in
 */
export const nodeAndPathToSelectedSearchItem = (
  node: HierarchyNodeOutputV1,
  nodePath: HierarchyNodeOutputV1[],
  hierarchyOutput: SelectedSearchItem,
): SelectedSearchItem => {
  const nodePathAsAncestors: SelectedSearchItem[] = [hierarchyOutput];
  for (let i = 0; i < nodePath.length; i++) {
    const pathNode = nodePath[i];
    const thisAncestorAsSelectedSearchItem = nodeToSelectedSearchItem(pathNode, _.cloneDeep(nodePathAsAncestors));
    nodePathAsAncestors.push(thisAncestorAsSelectedSearchItem);
  }
  return nodeToSelectedSearchItem(node, nodePathAsAncestors);
};

/**
 * Fetches the children of a hierarchy node, and return them as SelectedSearchItems with ancestors.
 * @param hierarchyId - id of the hierarchy
 * @param currentItemWithNode - current item with node and ancestors
 * @param currentNodePath - path from root to the current node
 */
export const getHierarchyLayerAsItemsWithAncestors = async (
  hierarchyId: string,
  currentItemWithNode: SelectedSearchItem,
  currentNodePath: HierarchyNodeOutputV1[],
): Promise<SelectedSearchItem[]> => {
  const columnIds = currentNodePath.length > 0 ? currentNodePath.map((node) => node.columnId) : undefined;
  const columnValues =
    currentNodePath.length > 0 ? currentNodePath.map((node) => node.itemMetadata?.id ?? node.displayName) : undefined;
  const response = await sqHierarchiesApi.getHierarchyNodes({
    id: hierarchyId,
    columnIds,
    columnValues,
  });
  const nodes = response.data.nodes;

  return (
    nodes?.map((node) => {
      const ancestors = [
        ...(currentItemWithNode.ancestors || []),
        {
          id: currentItemWithNode.id,
          name: currentItemWithNode.name,
          type: currentItemWithNode.type,
          node: currentItemWithNode.node,
        },
      ];
      return nodeToSelectedSearchItem(node, ancestors);
    }) || []
  );
};
